import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Stack } from '@chakra-ui/react';
import { useGenerateSignUpToken, useSignUpPassword } from 'hooks';
import { useTranslation } from 'localization';
import { routes } from 'statics';

import {
  AuthSuggestion,
  DesktopAuthHeader,
  SERVICE_CARD_BORDER_RADIUS,
} from 'components';

import { Form } from './components';

const Password: React.FC = () => {
  const { t } = useTranslation('component.auth.password');
  const [csrfToken, setCsrfToken] = React.useState<string>('');

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { mutateAsync: sendSignUpCode, isPending: csrfLoading } =
    useGenerateSignUpToken();

  const { mutateAsync: submit, isPending: isSubmitting } =
    useSignUpPassword(csrfToken);

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  const systemIdentifier = searchParams.get('system_identifier') || '';
  const navigateState =
    email && systemIdentifier
      ? {
          state: {
            client_email: email,
            system_identifier: systemIdentifier,
          },
        }
      : {};

  React.useEffect(() => {
    if (token) {
      sendSignUpCode({
        code: token,
        email: email,
      })
        .then((res) => {
          if (res?.token) {
            setSearchParams({ email: email });
            setCsrfToken(res.token);
          }
        })
        .catch(() => {
          navigate(routes.auth.signUp.url);
        });
    } else {
      navigate(routes.auth.signUp.url);
    }
  }, []);

  return (
    <Stack w="100%" align="stretch" gap={{ base: 6, md: 8 }}>
      <DesktopAuthHeader title={t('passwordToMagix')} titleAlign="center" />
      <Box
        bg="dark"
        borderWidth="1px"
        borderColor="rgba(255,255,255,0.08)"
        borderRadius={SERVICE_CARD_BORDER_RADIUS}
        px={{ base: 5, md: 7 }}
        py={{ base: 6, md: 8 }}
        w="100%"
      >
        <Form
          csrfLoading={csrfLoading}
          isSubmitting={isSubmitting}
          onButton={(data) =>
            submit({
              ...data,
              username: email,
              referrer: localStorage.getItem('referrer') || '',
            }).then(() => {
              navigate(routes.auth.signIn.url, navigateState);
              localStorage.removeItem('referrer');
            })
          }
        />
      </Box>
      <AuthSuggestion include={['sign-in']} />
    </Stack>
  );
};

export default Password;
